import { AccountPayer, FormValues } from "./types";
import {
  Field,
  FieldMetaProps,
  FieldProps,
  FormikErrors,
  FormikProps,
  yupToFormErrors,
} from "formik";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import FormContext from "../../context/FormContext";
import {
  Alert,
  Button,
  Column,
  Columns,
  Divider,
  Heading,
  Stack,
  Text,
  TextField,
  TextLink,
} from "@origin-digital/ods-core";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import GeneralTextField from "./FormComponents/GeneralTextField";
import { onlineFormAccountDetailsValidationSchema } from "./FormComponents/Validations";
import {
  isAnyServiceSetToTargetEicStatus,
  isOnlineAccountHolderDetailsUnchanged,
  sendEicComms,
} from "./helper";
import { EICStatusEnum } from "../Premises/types";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { AppContext } from "../../context/AppContext";
import dayjs from "dayjs";

const DATE_FORMAT = "DD/MM/YY";

interface IOnlineFormAccountHolderDetailsProps {
  values: FormValues;
  setErrors: (errors: FormikErrors<FormValues>) => void;
  savingBlock: boolean;
  setSavingBlock: (saving: boolean) => void;
  saveAccountBlock: (v: FormValues) => Promise<void>;
  homeUser: boolean;
  showSubTitle?: boolean;
  saveButtonText?: string;
  isLodgingClaim?: boolean;
}

const helperText = (
  <Stack>
    <Text variant="body-small" tone="neutralLight">
      We'll send the account holder a link to a form to provide their details
      and consent. This form must be submitted before a request can be lodged
      with the distributor.
    </Text>
    <Text variant="body-small" tone="neutralLight">
      In the meantime, you can still submit this request and it will be
      automatically lodged once we receive the account holder details.
    </Text>
  </Stack>
);

const ReadOnlyOnlineAccountHolderDetails = ({
  isCommsSent,
  commsSentAlert,
  onSavedAccountDetails,
  savingBlock,
  isLodgingClaim,
  formValues,
}: {
  isCommsSent: boolean;
  commsSentAlert: React.JSX.Element;
  onSavedAccountDetails: () => Promise<void>;
  savingBlock?: boolean;
  isLodgingClaim?: boolean;
  formValues?: FormValues;
}) => {
  const {
    context: { user },
  } = useContext(AppContext);

  const notLodgedAlert = (
    <Alert data-id="alert" tone="info" variant="standard">
      <Text>
        Once you lodge your request(s), you'll receive an email with a link to a
        form to finalise account holder details.
      </Text>
    </Alert>
  );

  return (
    <Columns
      collapseBelow="md"
      space={{ xs: "none", md: "xlarge" }}
      alignY="stretch"
    >
      <Column width="2/3">
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
        >
          {isCommsSent ? commsSentAlert : notLodgedAlert}
          <Columns
            collapseBelow="md"
            space={{
              xs: "xlarge",
              md: "medium",
            }}
          >
            <Column>
              {/* First name */}
              <ReadOnlyFieldWrapper value={user?.first_name}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      First name
                    </Text>
                    <Text>
                      {formValues?.accountPrimaryFirstName
                        ? formValues.accountPrimaryFirstName
                        : user?.first_name}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End First name */}
            </Column>
            <Column>
              {/* Last name */}
              <ReadOnlyFieldWrapper value={user?.last_name}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Last name
                    </Text>
                    <Text>
                      {formValues?.accountPrimaryLastName
                        ? formValues.accountPrimaryLastName
                        : user?.last_name}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Last name */}
            </Column>
          </Columns>
          <ReadOnlyFieldWrapper value={user?.email}>
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  Email
                </Text>
                <Text>
                  {formValues?.accountPrimaryEmail
                    ? formValues.accountPrimaryEmail
                    : user?.email}
                </Text>
              </Stack>
              <Divider />
            </Stack>
          </ReadOnlyFieldWrapper>
          {isLodgingClaim && (
            <Button
              spinner={savingBlock}
              disabled={isCommsSent}
              onClick={() => {
                onSavedAccountDetails();
              }}
            >
              Lodge claim
            </Button>
          )}
        </Stack>
      </Column>
    </Columns>
  );
};

const OnlineFormAccountHolderDetails = (
  props: IOnlineFormAccountHolderDetailsProps
) => {
  const { formState, setFormState } = React.useContext(FormContext);
  const {
    values,
    setErrors,
    savingBlock,
    setSavingBlock,
    saveAccountBlock,
    homeUser,
    showSubTitle = true,
    saveButtonText,
    isLodgingClaim,
  } = props;
  const accountPayers = [
    values.electricityAccountPayer,
    values.gasLine?.accountPayer,
    values.gasMeter?.accountPayer,
    values.both?.accountPayer,
  ];
  const [isCommsSent, setIsCommsSent] = useState(false);
  const [isDetailsUnchanged, setIsDetailsUnchanged] = useState(true);
  const {
    setDisplayingSnackbar,
    onlineAccountHolderDetails,
    setOnlineAccountHolderDetails,
  } = React.useContext(FormContext);

  useEffect(() => {
    setIsCommsSent(
      isAnyServiceSetToTargetEicStatus(formState, EICStatusEnum.EIC_COMM_SENT)
    );
  }, []);

  useEffect(() => {
    setIsDetailsUnchanged(
      isOnlineAccountHolderDetailsUnchanged(values, onlineAccountHolderDetails)
    );
  }, [
    values.accountPrimaryFirstName,
    values.accountPrimaryLastName,
    values.accountPrimaryEmail,
  ]);

  const onSavedAccountDetails = async () => {
    await saveAccountBlock({
      ...values,
      sameAsConnectionAddress: values.sameAsConnectionAddress ?? true,
    });
    setDisplayingSnackbar({
      message: "Account holder details saved successfully.",
      tone: "positive",
    });
    setOnlineAccountHolderDetails({
      accountPrimaryFirstName: values.accountPrimaryFirstName,
      accountPrimaryLastName: values.accountPrimaryLastName,
      accountPrimaryEmail: values.accountPrimaryEmail,
    });
    setIsCommsSent(
      isAnyServiceSetToTargetEicStatus(formState, EICStatusEnum.EIC_COMM_SENT)
    );
  };

  const sendComms = async () => {
    setSavingBlock(true);
    const { valid, message } = await sendEicComms(
      values.id,
      null,
      values.accountPrimaryFirstName,
      values.accountPrimaryLastName,
      values.accountPrimaryEmail,
      "Notification re-sent.",
      "Account failed to save."
    );
    setDisplayingSnackbar({
      message,
      tone: valid ? "positive" : "caution",
    });
    setOnlineAccountHolderDetails({
      accountPrimaryFirstName: values.accountPrimaryFirstName,
      accountPrimaryLastName: values.accountPrimaryLastName,
      accountPrimaryEmail: values.accountPrimaryEmail,
    });
    setFormState((prevState) => ({
      ...prevState,
      commSentDateTime: new Date(),
    }));
    setIsCommsSent(true);
    setSavingBlock(false);
  };

  const commsSentAlert = (
    <Alert data-id="alert" tone="info" variant="standard">
      <Text>
        Notification was sent to account holder on{" "}
        {dayjs(formState.commSentDateTime).format(DATE_FORMAT)}.{" "}
        <TextLink
          href="#"
          onClick={(e) => {
            e.preventDefault();
            sendComms();
          }}
        >
          Resend?
        </TextLink>
      </Text>
    </Alert>
  );
  return (
    <>
      {homeUser &&
      accountPayers.includes(AccountPayer.SOMEONE_ELSE) === false ? (
        <ReadOnlyOnlineAccountHolderDetails
          isCommsSent={isCommsSent}
          commsSentAlert={commsSentAlert}
          onSavedAccountDetails={onSavedAccountDetails}
          savingBlock={savingBlock}
          isLodgingClaim={isLodgingClaim}
          formValues={values}
        />
      ) : (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
        >
          <FormFieldLayout text={helperText} multipleFields={true}>
            {!homeUser && showSubTitle && (
              <Heading variant="h4" weight="medium">
                Account holder details
              </Heading>
            )}
            <Columns collapseBelow="md">
              <Column>
                <Field
                  id="account-primary-first-name"
                  name="accountPrimaryFirstName"
                  label="First Name"
                  component={GeneralTextField}
                />
              </Column>
              <Column>
                <Field
                  id="account-last-name"
                  name="accountPrimaryLastName"
                  label="Last Name"
                  component={GeneralTextField}
                />
              </Column>
            </Columns>
            <Field name="accountPrimaryEmail" id="account-primary-email">
              {({
                field,
                meta,
              }: {
                field: FormikProps<FieldProps>;
                meta: FieldMetaProps<FormValues>;
              }) => (
                <TextField
                  {...field}
                  id="account-primary-email"
                  variant="outlined"
                  label="Email"
                  hideHelperTextSpace
                  error={Boolean(meta.error)}
                  helperText={meta.error}
                />
              )}
            </Field>
            {isCommsSent && isDetailsUnchanged && commsSentAlert}
          </FormFieldLayout>
          <Button
            spinner={savingBlock}
            disabled={Boolean(isCommsSent && isDetailsUnchanged)}
            onClick={() => {
              setErrors({});
              onlineFormAccountDetailsValidationSchema()
                .validate(values, {
                  abortEarly: false,
                })
                .then(() => {
                  onSavedAccountDetails();
                })
                .catch((e) => {
                  setErrors(yupToFormErrors(e));
                });
            }}
          >
            {saveButtonText ?? "Save Account"}
          </Button>
        </Stack>
      )}
    </>
  );
};

export default OnlineFormAccountHolderDetails;
